/**
 * Pointer / keyboard interaction, per the SPEC table.
 *
 *   wheel        zoom the camera about the cursor (0.5x .. 20x of "fit")
 *   drag         spin the ring, with inertia and a soft snap to month borders
 *   drag a float move it; on release it pins where you dropped it
 *   drop a float on a band  put the entry in that circle instead of pinning it
 *   dbl a float  unpin, back to auto layout — counted from pointer releases
 *   press its pin  the same thing, for anyone who would never guess the above
 *   click a cluster  zoom about it until it dissolves
 *   click a band, or a circle's own ring line  select that circle
 *   Space+drag   pan the camera (grab cursor while Space is held)
 *   pinch        zoom + pan; one finger spins
 *   dbl-click    on a month: animate that month to the top
 *   Home         reset: fit the ring, Jan up
 *   ← / →        select the previous / next entry by date, and focus its card
 *   Enter        open the focused (or selected) card, as a click would
 *   Escape       deselect
 *
 * Spinning and panning never re-render the scene: they push a new ViewState
 * into `Scene.applyView`, which touches layer transforms. Dragging a float does
 * re-pack (the other floats have to make room) — about 4 ms for a year of
 * events, so once per pointer move is fine.
 *
 * Every camera animation — the month tween, the cluster zoom, Home, the snap,
 * inertia — asks `reducedMotion()` first and jump-cuts for a reader who has
 * asked for less motion, the same check the ring tween makes.
 */
import {
  RING_RADIUS,
  angleDelta,
  easeInOutCubic,
  floatAngularExtent,
  fitCamera,
  monthArc,
  normalizeAngle,
  polarToXY,
  rotatePoint,
  screenToWorld,
  worldToScreen,
  xyToPolar,
  zoomAbout,
  type Camera,
  type FloatPin,
  type Point,
  type ViewState,
} from '@circle/shared';
import {
  FIT_HEADROOM,
  FIT_PADDING,
  MAX_SLIDE_FLOAT_WIDTHS,
  MIN_SLIDE_RAD,
  type Scene,
  type SceneEntry,
  type SceneEntryOf,
} from './scene';
import { ROOT_RING_ID } from './rings';
import { prefersReducedMotion } from './motion';
import { floatSizing, type FloatSizing } from '../fragments/types';
import type { FragState } from '../fragments/types';

export { FIT_HEADROOM } from './scene';

export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 20;
/** Soft-snap window around a month boundary. */
export const SNAP_TOLERANCE = (3 * Math.PI) / 180;

/**
 * Below this day pitch (screen px) neighbouring ring marks share screen space
 * and `entryAt` re-decides a mark hit by nearest centre. Above it the targets
 * cannot overlap, so whatever the hit test found is already the answer.
 */
export const MARK_PILE_PITCH_PX = 30;

const DRAG_SLOP = 4;

/**
 * Two presses on the same float this close together, in milliseconds and in
 * screen pixels, mean "unpin".
 *
 * `dblclick` cannot be used for it. The compatibility mouse events only arrive
 * if the element that was pressed is still in the document at release, and a
 * float is a node the scene swaps whenever its state changes; touch never gets
 * them at all. Counting pointer releases works on both, and owes nothing to
 * what the DOM did in between.
 */
export const DOUBLE_TAP_MS = 350;
export const DOUBLE_TAP_SLOP = 6;

/** The unpin control a pinned float draws over its pin glyph. */
export const UNPIN_ROLE = 'unpin';
const FRICTION_PER_FRAME = 0.94;
const MIN_SPIN_SPEED = 2e-5; // rad/ms
const SNAP_MS = 220;
const MONTH_TWEEN_MS = 460;
const ZOOM_TWEEN_MS = 420;

/** A dropped float may sit anywhere between here and here, in world units. */
const PIN_MIN_RADIUS = RING_RADIUS * 0.12;
const PIN_MAX_RADIUS = RING_RADIUS * 4;

/** However much chrome is open, the camera still aims at a box this big. */
const MIN_FREE_PX = 80;

/**
 * Fit the scene to the viewport.
 *
 * `worldRadius` is what has to be on screen: `RING_RADIUS` for a calendar with
 * one ring, and the bounding circle of every placement once the circles have
 * split onto orbits (docs/CIRCLES.md §2). It is measured about the world origin
 * rather than about the rings' own box, because the whole scene turns about the
 * origin — a camera fitted to the box would clip the layout a quarter turn on.
 */
export function computeFit(width: number, height: number, worldRadius = RING_RADIUS): Camera {
  const fit = fitCamera(width, height, FIT_PADDING);
  const spread = worldRadius > 0 ? RING_RADIUS / worldRadius : 1;
  return { ...fit, scale: (fit.scale * spread) / FIT_HEADROOM };
}

/**
 * What the chrome is covering, in CSS pixels, so a fit can aim at what is left.
 *
 * The side panel is a column down the right on a desk and a sheet across the
 * bottom on a phone; either way the ring underneath it is not visible, and
 * fitting to the whole `<svg>` centred the ring behind the panel and drew it
 * smaller than it needed to be.
 */
export interface ViewportInsets {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
}

/**
 * How far outside the ring line a card reaches, in screen pixels.
 *
 * A card is laid out at `ringRadius + gap + height/2` and drawn at a constant
 * size on screen, so what it needs is a band of *pixels* rather than a fraction
 * of the ring — which is exactly why `FIT_HEADROOM`, being a fraction, runs out
 * on a phone. The gap is world-constant with a pixel floor, the same two
 * numbers the scene lays the floats out from.
 */
export function cardBandPx(sizing: FloatSizing, scale: number): number {
  const gap = Math.max(sizing.gap * scale, sizing.gapMinPx);
  return gap + Math.max(sizing.height, sizing.clusterHeight);
}

/** However much of the box the cards ask for, the rings keep this much of it. */
const MIN_RING_SHARE = 0.25;

/**
 * Fit `worldRadius` into the part of the viewport nothing is covering.
 *
 * `bandPx` is room to leave outside the rings for whatever hangs off them — the
 * cards. It is only ever a tightening: the fit is the smaller of the plain one
 * and the one that leaves the band, so a desk, where the proportional headroom
 * is already more than a card needs, is left exactly where it was.
 */
export function computeFitInto(
  width: number,
  height: number,
  worldRadius: number,
  insets: ViewportInsets = {},
  bandPx = 0,
): Camera {
  const left = Math.max(0, insets.left ?? 0);
  const right = Math.max(0, insets.right ?? 0);
  const top = Math.max(0, insets.top ?? 0);
  const bottom = Math.max(0, insets.bottom ?? 0);
  // A panel wider than the window would leave nothing to aim at; keep a box.
  const free = {
    width: Math.max(MIN_FREE_PX, width - left - right),
    height: Math.max(MIN_FREE_PX, height - top - bottom),
  };
  const fit = computeFit(free.width, free.height, worldRadius);
  const scale = bandPx > 0 ? Math.min(fit.scale, bandFitScale(free, worldRadius, bandPx)) : fit.scale;
  // `computeFit` centres in the box it was given; the camera translate is in
  // the viewport's frame, so the box's own corner goes back on.
  return { ...fit, scale, tx: left + fit.tx, ty: top + fit.ty };
}

/** The scale at which the rings *and* a `bandPx` margin round them fill the box. */
function bandFitScale(free: { width: number; height: number }, worldRadius: number, bandPx: number): number {
  if (!(worldRadius > 0)) return Infinity;
  const usable = Math.max(1, Math.min(free.width, free.height) - 2 * FIT_PADDING);
  // A viewport too short to hold a card at all still has to show the rings:
  // below this the cards are allowed to run off rather than the ring vanish.
  const room = Math.max(usable - 2 * bandPx, usable * MIN_RING_SHARE);
  return room / (2 * worldRadius);
}

/**
 * The fit that also holds the cards.
 *
 * The band is measured in pixels and the scale is what turns pixels into world,
 * so the two chase each other: a couple of rounds settle it, and the smallest
 * scale seen is the safe one — a smaller camera asks the band for less than it
 * was already given.
 */
export function computeFitWithCards(
  width: number,
  height: number,
  worldRadius: number,
  insets: ViewportInsets,
  sizing: FloatSizing,
): Camera {
  let fit = computeFitInto(width, height, worldRadius, insets);
  let smallest = fit;
  for (let i = 0; i < 3; i++) {
    fit = computeFitInto(width, height, worldRadius, insets, cardBandPx(sizing, fit.scale));
    if (fit.scale < smallest.scale) smallest = fit;
  }
  return smallest;
}

export function clampScale(scale: number, fitScale: number): number {
  return Math.min(Math.max(scale, fitScale * MIN_ZOOM), fitScale * MAX_ZOOM);
}

/** Rotation that brings a ring angle to 12 o'clock. */
export function rotationToTop(angle: number): number {
  return normalizeAngle(-angle);
}

/** Nearest month-boundary rotation, if it is within `tolerance`. */
export function snapRotation(
  rotation: number,
  year: number,
  tolerance = SNAP_TOLERANCE,
): number | undefined {
  let best: number | undefined;
  let bestAbs = Infinity;
  for (let m = 1; m <= 12; m++) {
    const target = rotationToTop(monthArc(year, m).start);
    const d = Math.abs(angleDelta(rotation, target));
    if (d < bestAbs) {
      bestAbs = d;
      best = target;
    }
  }
  return bestAbs <= tolerance ? best : undefined;
}

export function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

// --- pin coordinates --------------------------------------------------------

/**
 * Where a ring sits in the world — its centre, unrotated. The root's is the
 * origin, which is why it may be left out.
 */
export interface RingOrigin {
  cx: number;
  cy: number;
}

const WORLD_ORIGIN: RingOrigin = { cx: 0, cy: 0 };

/**
 * Screen point → pin on `ring`.
 *
 * A pin is polar about the ring it belongs to, in world units, so it must be
 * free of the camera, of the rotation, *and* of where the ring itself stands:
 * undo the camera, undo the spin, then move the origin to the ring's centre.
 * A ring out on an orbit is the case that needs the last step — measured about
 * the world origin instead, a drag is exact where it was grabbed and drifts
 * everywhere else. The inverse is {@link pinToScreen}; on every ring, at every
 * angle and zoom, the two round-trip exactly.
 */
export function screenToPin(
  view: ViewState,
  screen: Point,
  ring: RingOrigin = WORLD_ORIGIN,
): FloatPin {
  const world = screenToWorld(view, screen);
  const unrotated = rotatePoint(world, -view.rotation);
  const polar = xyToPolar(unrotated.x - ring.cx, unrotated.y - ring.cy);
  return { angle: polar.angle, radius: polar.radius };
}

export function pinToScreen(
  view: ViewState,
  pin: FloatPin,
  ring: RingOrigin = WORLD_ORIGIN,
): Point {
  const local = polarToXY(pin.angle, pin.radius);
  const world = { x: local.x + ring.cx, y: local.y + ring.cy };
  return worldToScreen(view, rotatePoint(world, view.rotation));
}

export function clampPin(pin: FloatPin): FloatPin {
  return {
    angle: normalizeAngle(pin.angle),
    radius: Math.min(PIN_MAX_RADIUS, Math.max(PIN_MIN_RADIUS, pin.radius)),
  };
}

// --- controller -------------------------------------------------------------

export interface InputOptions {
  scene: Scene;
  /** Element that owns cursor classes (defaults to the scene's <svg>). */
  target?: SVGSVGElement;
  initial: ViewState;
  /** `committed` is true at the end of a gesture — the moment worth persisting. */
  onView: (view: ViewState, committed: boolean) => void;
  onSelect?: (entry: SceneEntry | null) => void;
  onHover?: (entry: SceneEntry | null, screen: Point) => void;
  /**
   * A float was dropped (`pin`) or double-clicked back to auto (`null`).
   * `ringId` says which circle's ring it was on — `'root'` for a calendar with
   * no circles, which is why it comes last and may be ignored.
   */
  onPin?: (eventId: string, pin: FloatPin | null, ringId: string) => void;
  /**
   * A card was dropped on a circle's band, or on a circle's own ring line: the
   * entry wants to join that circle, and has *not* been pinned. Also raised as
   * a `circle:drop` CustomEvent on the `<svg>` for a page that only has the
   * element. Ignored for a circle the entry is already in.
   */
  onDropOnCircle?: (eventId: string, tagId: string) => void;
  /** A cluster was clicked; the camera is already animating in. */
  onClusterExpand?: (entry: SceneEntry) => void;
  /** A reader who has asked for less motion gets the destination, not the journey. */
  reducedMotion?: () => boolean;
}

/**
 * `detail` of the `circle:select` CustomEvent. `eventIds` is empty for a
 * fragment that stands for no entry; `tagId` names the circle a band or a
 * ring line stands for, and is null for the root and for anything else.
 */
export interface SceneSelectDetail {
  id: string | null;
  kind: SceneEntry['kind'] | null;
  eventIds: string[];
  ringId: string | null;
  tagId: string | null;
}

/** `detail` of the `circle:drop` CustomEvent: a card let go over a circle. */
export interface SceneDropDetail {
  eventId: string;
  tagId: string;
}

interface Tween {
  from: number;
  to: number;
  start: number;
  duration: number;
}

interface ZoomTween {
  from: number;
  to: number;
  about: Point;
  world: Point;
  start: number;
  duration: number;
}

interface FloatDrag {
  entryId: string;
  eventId: string;
  /** The ring the card hangs off; `'root'` unless the circles have split. */
  ringId: string;
  /**
   * That ring's centre, read once at grab time: pins are polar about their own
   * ring, and a ring does not move under a drag.
   */
  origin: RingOrigin;
  /** Pointer polar → float polar, captured at grab time so the card never jumps. */
  dAngle: number;
  dRadius: number;
  moved: boolean;
}

type Mode = 'idle' | 'spin' | 'pan' | 'pinch' | 'float';

export class InputController {
  private readonly scene: Scene;
  private readonly svg: SVGSVGElement;
  private readonly opts: InputOptions;
  private readonly reducedMotion: () => boolean;

  private view: ViewState;
  private width = 1;
  private height = 1;
  private insets: ViewportInsets = {};
  private fitScale = 1;

  private mode: Mode = 'idle';
  private spaceHeld = false;
  private readonly pointers = new Map<number, Point>();
  private last: Point = { x: 0, y: 0 };
  private downAt: Point = { x: 0, y: 0 };
  private moved = false;
  private lastAngle = 0;
  private samples: { t: number; d: number }[] = [];
  private pinchDist = 0;
  private pinchMid: Point = { x: 0, y: 0 };
  private drag: FloatDrag | undefined;
  /** The element actually pressed, before pointer capture retargets everything. */
  private downTarget: Element | null = null;
  private lastTap: { entryId: string; at: number; point: Point } | undefined;

  private raf = 0;
  private spinVelocity = 0;
  private lastFrame = 0;
  private tween: Tween | undefined;
  private zoomTween: ZoomTween | undefined;

  private hoverId: string | null = null;
  /** Where the pointer was when the hover was last decided; a tooltip sits here. */
  private hoverPoint: Point = { x: 0, y: 0 };
  private selectedId: string | null = null;
  /**
   * The entry the arrow keys are on. Selection is per card, and a cluster's
   * card stands for several entries, so the keys keep their own place in the
   * year; it is only trusted while the selected card still holds that entry.
   */
  private keyCursor: string | null = null;

  constructor(options: InputOptions) {
    this.opts = options;
    this.scene = options.scene;
    this.svg = options.target ?? options.scene.svg;
    this.reducedMotion = options.reducedMotion ?? prefersReducedMotion;
    this.view = options.initial;
    this.scene.onEntriesChanged = this.onEntriesChanged;
    this.attach();
  }

  // --- public API -----------------------------------------------------------

  get currentView(): ViewState {
    return this.view;
  }

  setViewport(width: number, height: number): void {
    this.width = width;
    this.height = height;
    this.fitScale = this.fit().scale;
  }

  /** What the chrome is covering. A fit aims at the rest. */
  setInsets(insets: ViewportInsets): void {
    this.insets = insets;
    this.fitScale = this.fit().scale;
  }

  /**
   * Fit for what is actually on screen: every ring, not only the root one, its
   * cards with it, and into the part of the viewport the panel is not sitting
   * on.
   */
  private fit(): Camera {
    return computeFitWithCards(
      this.width,
      this.height,
      this.scene.boundsRadius,
      this.insets,
      floatSizing(this.scene.currentTheme),
    );
  }

  setView(view: ViewState, committed = true): void {
    this.view = view;
    this.scene.applyView(view);
    this.opts.onView(view, committed);
    if (committed) this.scene.syncRotation(view.rotation);
  }

  /**
   * The ring has come to rest. Anything whose *readable* orientation depends on
   * the rotation — the month labels — gets a chance to re-render here, once,
   * rather than on every frame of a spin.
   */
  settle(): void {
    this.scene.syncRotation(this.view.rotation);
  }

  /** Fit the ring to the viewport with January at the top. */
  reset(animate = true): void {
    this.stopAnimation();
    const fit = this.fit();
    this.fitScale = fit.scale;
    const next = { ...this.view, ...fit };
    if (!animate || this.reducedMotion()) {
      this.setView({ ...next, rotation: 0 });
      return;
    }
    this.setView(next, false);
    this.startRotationTween(0, MONTH_TWEEN_MS);
  }

  /** Turn so `month` (1..12) sits at the top. */
  bringMonthToTop(month: number): void {
    const arc = monthArc(this.view.year, month);
    this.turnTo(rotationToTop((arc.start + arc.end) / 2), MONTH_TWEEN_MS);
  }

  /** The rotation tween, or the destination outright for a reader who wants no journey. */
  private turnTo(rotation: number, duration: number): void {
    if (this.reducedMotion()) {
      this.stopAnimation();
      this.setView({ ...this.view, rotation: normalizeAngle(rotation) }, true);
      return;
    }
    this.startRotationTween(rotation, duration);
  }

  /** The zoom tween about `about`, or the destination outright. */
  private zoomTo(scale: number, about: Point): void {
    if (this.reducedMotion()) {
      this.stopAnimation();
      const world = screenToWorld(this.view, about);
      this.setView(
        { ...this.view, scale, tx: about.x - world.x * scale, ty: about.y - world.y * scale },
        true,
      );
      return;
    }
    this.startZoomTween(scale, about);
  }

  /**
   * Selecting the entry that is already selected still notifies. The flags are
   * already right, so there is nothing to re-render — but a listener that closed
   * its own panel without telling us (a detail panel closing itself after a
   * save) would otherwise be stuck: the scene still holds the selection, so the
   * next click on the same float would be swallowed and the panel never reopen.
   * Selecting nothing twice is genuinely nothing, and stays silent.
   */
  select(id: string | null): void {
    if (this.selectedId === id && id === null) return;
    if (this.selectedId !== id) {
      if (this.selectedId) this.applyFlag(this.selectedId, 'selected', false);
      this.selectedId = id;
      if (id) this.applyFlag(id, 'selected', true);
    }
    const entry = id ? (this.scene.entry(id) ?? null) : null;
    this.opts.onSelect?.(entry);
    this.emitSelect(entry);
  }

  get selection(): string | null {
    return this.selectedId;
  }

  /**
   * Phase 6 hook: a DOM event carrying enough to open a detail panel without
   * reaching into the scene. `detail.events` is [] for non-event fragments.
   */
  private emitSelect(entry: SceneEntry | null): void {
    const eventIds = entry?.eventIds ?? (entry?.eventId ? [entry.eventId] : []);
    const detail: SceneSelectDetail & { events: unknown[] } = {
      id: entry?.id ?? null,
      kind: entry?.kind ?? null,
      eventIds,
      ringId: entry?.ringId ?? null,
      tagId: entry ? circleOf(entry) : null,
      events: eventIds.map((id) => this.scene.eventById(id)).filter(Boolean),
    };
    this.svg.dispatchEvent(new CustomEvent('circle:select', { bubbles: true, detail }));
  }

  destroy(): void {
    this.stopAnimation();
    if (this.scene.onEntriesChanged === this.onEntriesChanged) {
      this.scene.onEntriesChanged = undefined;
    }
    this.svg.removeEventListener('pointerdown', this.onPointerDown);
    this.svg.removeEventListener('pointermove', this.onPointerMove);
    this.svg.removeEventListener('pointerup', this.onPointerUp);
    this.svg.removeEventListener('pointercancel', this.onPointerUp);
    this.svg.removeEventListener('pointerleave', this.onPointerLeave);
    this.svg.removeEventListener('wheel', this.onWheel);
    this.svg.removeEventListener('dblclick', this.onDoubleClick);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
  }

  private attach(): void {
    this.svg.addEventListener('pointerdown', this.onPointerDown);
    this.svg.addEventListener('pointermove', this.onPointerMove);
    this.svg.addEventListener('pointerup', this.onPointerUp);
    this.svg.addEventListener('pointercancel', this.onPointerUp);
    this.svg.addEventListener('pointerleave', this.onPointerLeave);
    this.svg.addEventListener('wheel', this.onWheel, { passive: false });
    this.svg.addEventListener('dblclick', this.onDoubleClick);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
  }

  // --- geometry helpers -----------------------------------------------------

  private toLocal(e: { clientX: number; clientY: number }): Point {
    const rect = this.svg.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  /** Pointer angle about the ring centre, in screen space. */
  private angleAt(p: Point): number {
    return xyToPolar(p.x - this.view.tx, p.y - this.view.ty).angle;
  }

  /**
   * Pointer capture, defensively. A synthetic pointer event — a test, an
   * automation tool, an assistive technology — carries an id no browser ever
   * issued, and capturing it throws `NotFoundError`. Losing the capture costs
   * a gesture that leaves the element; throwing costs the gesture entirely.
   */
  private capture(pointerId: number): void {
    try {
      this.svg.setPointerCapture?.(pointerId);
    } catch {
      /* not a real pointer */
    }
  }

  private release(pointerId: number): void {
    try {
      if (this.svg.hasPointerCapture?.(pointerId)) this.svg.releasePointerCapture(pointerId);
    } catch {
      /* already gone */
    }
  }

  // --- pointer --------------------------------------------------------------

  private readonly onPointerDown = (e: PointerEvent): void => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    // Read the target before capturing: from here on every pointer event on
    // this gesture retargets to the <svg>, and which part of a card was pressed
    // is no longer answerable.
    this.downTarget = e.target instanceof Element ? e.target : null;
    this.capture(e.pointerId);
    const p = this.toLocal(e);
    this.pointers.set(e.pointerId, p);
    this.stopAnimation();

    if (this.pointers.size >= 2) {
      this.endFloatDrag(false);
      this.beginPinch();
      return;
    }

    this.last = p;
    this.downAt = p;
    this.moved = false;
    this.samples = [];

    if (this.spaceHeld) {
      this.mode = 'pan';
      this.svg.classList.add('is-panning');
      return;
    }

    // A float under the pointer always wins: pressing a card never spins the
    // ring, on mouse or on touch. Everything else — background, ring furniture,
    // clusters — spins.
    const entry = this.entryFor(e.target);
    if (entry?.kind === 'float' && this.beginFloatDrag(entry, p)) return;

    this.mode = 'spin';
    this.lastAngle = this.angleAt(p);
    this.svg.classList.add('is-spinning');
  };

  private readonly onPointerMove = (e: PointerEvent): void => {
    const p = this.toLocal(e);
    if (!this.pointers.has(e.pointerId)) {
      this.hoverAt(e, p);
      return;
    }
    this.pointers.set(e.pointerId, p);

    if (Math.hypot(p.x - this.downAt.x, p.y - this.downAt.y) > DRAG_SLOP) this.moved = true;

    switch (this.mode) {
      case 'pinch':
        this.updatePinch();
        break;
      case 'float':
        this.updateFloatDrag(p);
        break;
      case 'pan': {
        this.setView(
          { ...this.view, tx: this.view.tx + (p.x - this.last.x), ty: this.view.ty + (p.y - this.last.y) },
          false,
        );
        break;
      }
      case 'spin': {
        const angle = this.angleAt(p);
        const d = angleDelta(this.lastAngle, angle);
        this.lastAngle = angle;
        this.pushSample(d);
        this.setView({ ...this.view, rotation: normalizeAngle(this.view.rotation + d) }, false);
        break;
      }
      default:
        break;
    }
    this.last = p;
  };

  private readonly onPointerUp = (e: PointerEvent): void => {
    if (!this.pointers.has(e.pointerId)) return;
    this.pointers.delete(e.pointerId);
    this.release(e.pointerId);

    if (this.mode === 'pinch') {
      if (this.pointers.size === 1) {
        const [only] = [...this.pointers.values()];
        this.mode = 'spin';
        this.last = only!;
        this.downAt = only!;
        this.moved = true;
        this.lastAngle = this.angleAt(only!);
        this.samples = [];
      } else if (this.pointers.size === 0) {
        this.endGesture();
      }
      return;
    }

    if (this.pointers.size > 0) return;

    if (this.mode === 'float') {
      const drag = this.drag;
      const dragged = drag?.moved ?? false;
      // Let go over a circle: the card joins it rather than pinning there.
      const circle = dragged && drag ? this.circleUnder(drag, e) : undefined;
      this.endFloatDrag(circle === undefined);
      this.mode = 'idle';
      this.svg.classList.remove('is-dragging-float');
      if (!dragged && drag) this.releaseFloat(drag, e);
      if (circle !== undefined && drag) this.dropOnCircle(drag.eventId, circle);
      // A drag is not a tap, and must not pair with the press before it.
      if (dragged) this.lastTap = undefined;
      return;
    }

    const wasSpin = this.mode === 'spin';
    const moved = this.moved;
    this.endGesture();

    if (wasSpin && !moved) {
      this.clickAt(e);
      return;
    }
    if (wasSpin) this.launchInertia();
  };

  private readonly onPointerLeave = (): void => {
    if (this.mode === 'idle') this.setHover(null, { x: 0, y: 0 });
  };

  private endGesture(): void {
    this.mode = 'idle';
    this.svg.classList.remove('is-spinning', 'is-panning');
    this.opts.onView(this.view, true);
    this.settle();
  }

  private pushSample(d: number): void {
    const t = performance.now();
    this.samples.push({ t, d });
    while (this.samples.length > 8 || (this.samples[0] && t - this.samples[0].t > 120)) {
      this.samples.shift();
    }
  }

  // --- float drag (phase 4) -------------------------------------------------

  private beginFloatDrag(entry: SceneEntry, p: Point): boolean {
    const eventId = entry.eventId;
    const placed = this.scene.placement(entry.id);
    if (!eventId || !placed) return false;

    // `placed` is polar about the card's own ring, so the pointer has to be
    // read in that frame too or the grab offset is measured across two. The
    // ring is the *placement's*, not the circle's: a merged child is a band on
    // its parent's line, and a pin made there belongs to the parent's ring.
    const ringId = this.scene.pinRingFor(entry.ringId ?? ROOT_RING_ID);
    const origin = this.ringOrigin(ringId);
    const grab = screenToPin(this.view, p, origin);
    this.drag = {
      entryId: entry.id,
      eventId,
      ringId,
      origin,
      dAngle: angleDelta(grab.angle, placed.angle),
      dRadius: placed.radius - grab.radius,
      moved: false,
    };
    this.mode = 'float';
    this.svg.classList.add('is-dragging-float');
    return true;
  }

  private updateFloatDrag(p: Point): void {
    const drag = this.drag;
    if (!drag) return;
    if (!drag.moved && !this.moved) return;
    if (!drag.moved) {
      drag.moved = true;
      // Now the card is leaving the pointer and the packing around it with it,
      // so whatever the hover was describing has stopped being true. Not on the
      // press: clearing a float's hover rebuilds it, and a node replaced
      // between press and release is a node the browser will not report a click
      // or a double-click on.
      this.setHover(null, p);
    }
    this.scene.setLivePin(drag.eventId, this.pinAt(p, drag), drag.ringId);
  }

  /**
   * A press on a float that went nowhere: the unpin control, the second half of
   * a double-tap, or an ordinary click.
   */
  private releaseFloat(drag: FloatDrag, e: PointerEvent): void {
    const pinned = this.scene.entry(drag.entryId)?.state.pinned ?? false;
    if (pinned && this.downTarget?.closest(`[data-role="${UNPIN_ROLE}"]`)) {
      this.lastTap = undefined;
      this.unpin(drag.eventId, drag.ringId);
      return;
    }
    if (this.isDoubleTap(drag.entryId, this.toLocal(e)) && pinned) {
      this.unpin(drag.eventId, drag.ringId);
      return;
    }
    this.clickAt(e);
  }

  /**
   * Whether this release completes a pair. A tap that does not is remembered so
   * the next one can pair with it; one that does clears the memory, so three
   * taps are one double and one single rather than two doubles.
   */
  private isDoubleTap(entryId: string, point: Point): boolean {
    const at = performance.now();
    const last = this.lastTap;
    const paired =
      last !== undefined &&
      last.entryId === entryId &&
      at - last.at <= DOUBLE_TAP_MS &&
      Math.hypot(point.x - last.point.x, point.y - last.point.y) < DOUBLE_TAP_SLOP;
    this.lastTap = paired ? undefined : { entryId, at, point };
    return paired;
  }

  private pinAt(p: Point, drag: FloatDrag): FloatPin {
    const pointer = screenToPin(this.view, p, drag.origin);
    return clampPin({
      angle: pointer.angle + drag.dAngle,
      radius: pointer.radius + drag.dRadius,
    });
  }

  /** Where a ring stands, for turning its pins into world coordinates. */
  private ringOrigin(ringId: string): RingOrigin {
    const ring = this.scene.ringTransform(ringId);
    return ring ? { cx: ring.cx, cy: ring.cy } : WORLD_ORIGIN;
  }

  /**
   * The circle under a dropped card, if any: a band, or a ring line other than
   * the root's. The card itself is under the pointer — it followed it here —
   * so its slot is taken out of hit testing for the one question. A circle the
   * entry is already in is not an answer; the drop is then an ordinary pin.
   */
  private circleUnder(drag: FloatDrag, e: { clientX: number; clientY: number }): string | undefined {
    if (typeof document.elementFromPoint !== 'function') return undefined;
    const slot = this.scene.entry(drag.entryId)?.parent;
    const was = slot?.style.pointerEvents ?? '';
    if (slot) slot.style.pointerEvents = 'none';
    try {
      const hit = this.entryFor(document.elementFromPoint(e.clientX, e.clientY));
      const tagId = hit ? circleOf(hit) : null;
      if (!tagId) return undefined;
      const event = this.scene.eventById(drag.eventId);
      return event?.tagIds?.includes(tagId) ? undefined : tagId;
    } finally {
      if (slot) slot.style.pointerEvents = was;
    }
  }

  private dropOnCircle(eventId: string, tagId: string): void {
    this.opts.onDropOnCircle?.(eventId, tagId);
    const detail: SceneDropDetail = { eventId, tagId };
    this.svg.dispatchEvent(new CustomEvent('circle:drop', { bubbles: true, detail }));
  }

  /** Release: `commit` false abandons the drag (a second finger landed, or a circle took the card). */
  private endFloatDrag(commit: boolean): void {
    const drag = this.drag;
    this.drag = undefined;
    if (!drag) return;
    const pin = drag.moved ? this.pinAt(this.last, drag) : null;
    this.scene.setLivePin(drag.eventId, null, drag.ringId);
    if (!commit || !pin) return;
    this.scene.setPin(drag.eventId, pin, drag.ringId);
    this.opts.onPin?.(drag.eventId, pin, drag.ringId);
  }

  /** Back to auto layout: the unpin control, or a double-tap on the card. */
  unpin(eventId: string, ringId: string = ROOT_RING_ID): void {
    const ring = this.scene.pinRingFor(ringId);
    this.scene.setPin(eventId, null, ring);
    this.opts.onPin?.(eventId, null, ring);
  }

  private beginPinch(): void {
    const [a, b] = [...this.pointers.values()];
    if (!a || !b) return;
    this.mode = 'pinch';
    this.svg.classList.remove('is-spinning');
    this.pinchDist = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    this.pinchMid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }

  private updatePinch(): void {
    const [a, b] = [...this.pointers.values()];
    if (!a || !b) return;
    const dist = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };

    const factor = dist / this.pinchDist;
    let camera: Camera = zoomAbout(this.view, mid, factor);
    camera = this.clampCamera(camera, mid);
    camera = {
      ...camera,
      tx: camera.tx + (mid.x - this.pinchMid.x),
      ty: camera.ty + (mid.y - this.pinchMid.y),
    };

    this.pinchDist = dist;
    this.pinchMid = mid;
    this.setView({ ...this.view, ...camera }, false);
  }

  /** Re-derive the camera when a zoom would leave the allowed scale band. */
  private clampCamera(camera: Camera, about: Point): Camera {
    const clamped = clampScale(camera.scale, this.fitScale);
    if (clamped === camera.scale) return camera;
    const world = screenToWorld(this.view, about);
    return { scale: clamped, tx: about.x - world.x * clamped, ty: about.y - world.y * clamped };
  }

  // --- wheel ----------------------------------------------------------------

  private readonly onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    this.stopAnimation();
    const cursor = this.toLocal(e);
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? this.height : 1;
    const factor = Math.exp((-e.deltaY * unit) / 420);
    const camera = this.clampCamera(zoomAbout(this.view, cursor, factor), cursor);
    this.setView({ ...this.view, ...camera }, true);
  };

  // --- clicks ---------------------------------------------------------------

  /**
   * Months only. A float's double-click is counted from its pointer releases
   * instead — see `isDoubleTap` — because the browser will not report one on a
   * card whose node was swapped mid-gesture, and reports none at all on touch.
   */
  private readonly onDoubleClick = (e: MouseEvent): void => {
    const entry = this.entryAt(e);
    if (entry?.kind === 'month') this.bringMonthToTop(entry.ctx.index);
  };

  /**
   * A press that went nowhere. Background and the root's own line deselect; a
   * band, or another circle's ring line, selects that circle — the one place a
   * circle is drawn on the ring is where it can be picked up.
   */
  private clickAt(e: PointerEvent): void {
    const entry = this.entryAt(e);
    if (!entry || (entry.kind === 'ring' && !circleOf(entry))) {
      this.select(null);
      return;
    }
    this.activate(entry);
  }

  /** What a click on `entry` does; Enter on a focused card does the same. */
  private activate(entry: SceneEntry): void {
    this.select(entry.id);
    if (entry.kind === 'cluster') this.expandCluster(entry);
  }

  /**
   * Expand a cluster by zooming the camera about it, rather than by locally
   * fanning its members out. Zoom is what dissolves clusters everywhere else in
   * the product — floats keep their screen size while the ring grows, so the
   * anchors spread — and reusing it means there is no second, special layout
   * mode to keep consistent, no expanded-state to remember across a re-pack,
   * and the view you land in is a real view you could have reached by
   * scrolling. The target scale is the smallest at which the run provably fits.
   */
  expandCluster(entry: SceneEntry): void {
    const placed = this.scene.placement(entry.id);
    if (!placed || placed.kind !== 'cluster') return;
    const about = this.screenPointOf(placed.position);
    const target = clampScale(this.dissolveScale(placed.memberIds), this.fitScale);
    const next = Math.max(target, Math.min(this.view.scale * 1.5, this.fitScale * MAX_ZOOM));
    this.opts.onClusterExpand?.(entry);
    this.zoomTo(next, about);
  }

  /** Screen point of an unrotated world point under the current view. */
  private screenPointOf(world: Point): Point {
    return worldToScreen(this.view, rotatePoint(world, this.view.rotation));
  }

  /**
   * Smallest camera scale at which `memberIds` can sit side by side over the arc
   * they span, given the slide budget. Bisected rather than solved: the slide
   * budget has a floor, so the closed form has cases and the bisection does not.
   */
  private dissolveScale(memberIds: string[]): number {
    const angles = memberIds
      .map((id) => this.scene.entryOf('ringMark', this.scene.marksFor(id)[0] ?? '')?.ctx.angle)
      .filter((a): a is number => typeof a === 'number');
    if (angles.length < 2) return this.view.scale * 2;
    const span = angularSpan(angles);
    const sizing = floatSizing(this.scene.currentTheme);
    const k = angles.length;

    const fits = (scale: number): boolean => {
      const extent = floatAngularExtent(sizing.width / scale, RING_RADIUS + sizing.gap);
      const slide = Math.max(MAX_SLIDE_FLOAT_WIDTHS * extent, MIN_SLIDE_RAD);
      return k * extent <= span + 2 * slide;
    };

    let lo = this.view.scale;
    let hi = this.fitScale * MAX_ZOOM;
    // Already geometrically fine at this zoom: the run is jammed by its
    // neighbours, not by its own span, so just take a clear step in.
    if (fits(lo)) return lo * 2;
    if (!fits(hi)) return hi;
    for (let i = 0; i < 40; i++) {
      const mid = Math.sqrt(lo * hi);
      if (fits(mid)) hi = mid;
      else lo = mid;
    }
    return hi * 1.25;
  }

  private entryFor(target: EventTarget | null): SceneEntry | undefined {
    if (!(target instanceof Element)) return undefined;
    const host = target.closest('[data-kind]');
    const id = host?.getAttribute('data-id');
    return id ? this.scene.entry(id) : undefined;
  }

  /**
   * The fragment under an event. `event.target` alone is not enough: once the
   * gesture has captured the pointer, every later pointer event — and the
   * compatibility mouse events that follow it, including dblclick — retargets
   * to the capturing <svg>, which carries no `data-kind`. Hit test the point
   * instead, and keep the target as the fallback (jsdom has no elementFromPoint).
   */
  private entryAt(e: { target: EventTarget | null; clientX: number; clientY: number }):
    | SceneEntry
    | undefined {
    const direct = this.entryFor(e.target);
    const hit =
      direct ??
      (typeof document.elementFromPoint === 'function'
        ? this.entryFor(document.elementFromPoint(e.clientX, e.clientY))
        : undefined);
    if (hit?.kind !== 'ringMark') return hit;
    return this.nearestMark(this.toLocal(e), hit);
  }

  /**
   * Which of several piled-up ring marks the pointer meant.
   *
   * Marks carry an invisible target a good deal wider than the dot, and at fit
   * zoom consecutive days are about six pixels apart — so a run of birthdays is
   * a stack of overlapping targets and the hit test hands back whichever was
   * painted last, not the one under the finger. Once the day pitch is tighter
   * than a target, re-decide by nearest mark centre. Ranking against the ring
   * line rather than the drawn dot (which is a theme's business, not the core's)
   * gives the same order — as long as the line is the one the mark is actually
   * on, which with a tree of rings means the mark's own ring and radius.
   */
  private nearestMark(pointer: Point, hit: SceneEntry): SceneEntry {
    const dayPitchPx = (RING_RADIUS * Math.PI * 2 * this.view.scale) / 365;
    if (dayPitchPx >= MARK_PILE_PITCH_PX) return hit;

    let best: SceneEntry = hit;
    let bestDistance = Infinity;
    for (const entry of this.scene.all()) {
      if (entry.kind !== 'ringMark') continue;
      const world = markWorld(entry, this.scene);
      if (!world) continue;
      const centre = this.screenPointOf(world);
      const distance = Math.hypot(centre.x - pointer.x, centre.y - pointer.y);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = entry;
      }
    }
    return best;
  }

  // --- hover ----------------------------------------------------------------

  private hoverAt(e: PointerEvent, local: Point): void {
    if (this.mode !== 'idle') return;
    this.setHover(this.entryFor(e.target)?.id ?? null, local);
  }

  private setHover(id: string | null, screen: Point): void {
    this.hoverPoint = screen;
    if (id === this.hoverId) {
      if (id) this.opts.onHover?.(this.scene.entry(id) ?? null, screen);
      return;
    }
    if (this.hoverId) this.applyFlag(this.hoverId, 'hover', false);
    this.hoverId = id;
    if (id) this.applyFlag(id, 'hover', true);
    this.opts.onHover?.(id ? (this.scene.entry(id) ?? null) : null, screen);
  }

  /**
   * A layout pass or a reload took some fragments away. The pointer has not
   * moved, so nothing else would notice that what it is hovering — the cluster
   * that just dissolved, the entry a save replaced — is gone, and whatever the
   * hover was showing would sit there describing nothing.
   */
  private readonly onEntriesChanged = (ids: string[]): void => {
    if (!this.hoverId) return;
    if (ids.includes(this.hoverId) || !this.scene.entry(this.hoverId)) {
      this.setHover(null, this.hoverPoint);
      return;
    }
    // It survived, but its contents may not have: say it again.
    this.opts.onHover?.(this.scene.entry(this.hoverId) ?? null, this.hoverPoint);
  };

  /**
   * Set hover/selected on a fragment plus everything the scene says moves with
   * it: a mark, its float (or the cluster that swallowed it), that card's
   * leader, and for a cluster every member's mark. Months dim their siblings.
   */
  private applyFlag(id: string, flag: 'hover' | 'selected', on: boolean): void {
    const entry = this.scene.entry(id);
    if (!entry) return;
    const patch: Partial<FragState> = flag === 'hover' ? { hover: on } : { selected: on };
    for (const linked of this.scene.linkedIds(id)) this.scene.setState(linked, patch);

    if (entry.kind === 'month' && flag === 'hover') {
      const index = entry.ctx.index;
      for (let m = 1; m <= 12; m++) {
        if (m !== index) this.scene.setState(`month:${m}`, { dimmed: on });
      }
    }
  }

  // --- keyboard -------------------------------------------------------------

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (isTyping(e.target)) return;
    if (e.code === 'Space') {
      e.preventDefault();
      if (!this.spaceHeld) {
        this.spaceHeld = true;
        this.svg.classList.add('is-pan-ready');
      }
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      this.reset();
      return;
    }
    if (e.key === 'Escape') {
      this.select(null);
      return;
    }
    // The rest are the ring's own keys, and only while the ring — or nothing —
    // has the focus: the panel's controls have arrow keys of their own.
    if (!this.inScene(e.target)) return;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      this.step(e.key === 'ArrowRight' ? 1 : -1);
      return;
    }
    if (e.key === 'Enter') {
      const id = this.focusedEntryId() ?? this.selectedId;
      const entry = id ? this.scene.entry(id) : undefined;
      if (!entry) return;
      e.preventDefault();
      this.activate(entry);
    }
  };

  /** Focus is on the ring, or on nothing at all. */
  private inScene(target: EventTarget | null): boolean {
    if (!(target instanceof Node)) return true;
    return target === document.body || target === document.documentElement || this.svg.contains(target);
  }

  /** The card whose slot has the focus, by the `data-slot` the scene stamps on it. */
  private focusedEntryId(): string | null {
    const active = document.activeElement;
    if (!active || !this.svg.contains(active)) return null;
    return active.closest('[data-slot]')?.getAttribute('data-slot') ?? null;
  }

  /**
   * Select the entry `delta` places along the year from the current one — the
   * selection's, or the focused card's — wrapping at the ends, and put the
   * focus on its card so a screen reader follows. With nothing current, → is
   * the first entry of the year and ← the last.
   */
  step(delta: 1 | -1): void {
    const ordered = this.scene.entriesByDate();
    if (ordered.length === 0) return;
    const currentId = this.focusedEntryId() ?? this.selectedId;
    const current = currentId ? this.scene.entry(currentId) : undefined;
    const members = current?.eventIds ?? (current?.eventId ? [current.eventId] : []);
    // On a cluster the keys stand on the member they arrived at; a cluster
    // picked with the pointer is left from its far end in the direction taken.
    const currentEvent =
      this.keyCursor && members.includes(this.keyCursor)
        ? this.keyCursor
        : delta > 0
          ? members.at(-1)
          : members[0];
    const at = currentEvent ? ordered.findIndex((event) => event.id === currentEvent) : -1;
    const next =
      at === -1
        ? ordered[delta > 0 ? 0 : ordered.length - 1]!
        : ordered[(at + delta + ordered.length) % ordered.length]!;
    const id = this.scene.ownerFor(next.id) ?? this.scene.marksFor(next.id)[0];
    if (!id) return;
    this.keyCursor = next.id;
    this.select(id);
    this.focusEntry(id);
  }

  private focusEntry(id: string): void {
    const entry = this.scene.entry(id);
    if (!entry || (entry.kind !== 'float' && entry.kind !== 'cluster')) return;
    entry.parent.focus({ preventScroll: true });
  }

  private readonly onKeyUp = (e: KeyboardEvent): void => {
    if (e.code !== 'Space') return;
    this.spaceHeld = false;
    this.svg.classList.remove('is-pan-ready');
    if (this.mode === 'pan') {
      this.mode = 'idle';
      this.svg.classList.remove('is-panning');
    }
  };

  // --- animation ------------------------------------------------------------

  private launchInertia(): void {
    const samples = this.samples;
    // No coasting for a reader who has asked for less motion: the ring stops
    // where the finger left it, then takes the snap if one is due.
    if (samples.length < 2 || this.reducedMotion()) {
      this.snapIfClose();
      return;
    }
    const first = samples[0]!;
    const lastSample = samples[samples.length - 1]!;
    const span = lastSample.t - first.t;
    if (span <= 0) {
      this.snapIfClose();
      return;
    }
    const total = samples.reduce((sum, s, i) => (i === 0 ? sum : sum + s.d), 0);
    this.spinVelocity = total / span;
    if (Math.abs(this.spinVelocity) < MIN_SPIN_SPEED) {
      this.snapIfClose();
      return;
    }
    this.lastFrame = performance.now();
    this.startFrames();
  }

  private snapIfClose(): void {
    const target = snapRotation(this.view.rotation, this.view.year);
    if (target === undefined) return;
    this.turnTo(target, SNAP_MS);
  }

  private startRotationTween(to: number, duration: number): void {
    this.spinVelocity = 0;
    this.zoomTween = undefined;
    this.tween = {
      from: this.view.rotation,
      to: this.view.rotation + angleDelta(this.view.rotation, to),
      start: performance.now(),
      duration,
    };
    this.lastFrame = this.tween.start;
    this.startFrames();
  }

  /** Zoom to `to`, keeping the world point under `about` fixed the whole way. */
  private startZoomTween(to: number, about: Point, duration = ZOOM_TWEEN_MS): void {
    this.spinVelocity = 0;
    this.tween = undefined;
    if (Math.abs(Math.log(to / this.view.scale)) < 1e-3) return;
    this.zoomTween = {
      from: this.view.scale,
      to,
      about,
      world: screenToWorld(this.view, about),
      start: performance.now(),
      duration,
    };
    this.lastFrame = this.zoomTween.start;
    this.startFrames();
  }

  private startFrames(): void {
    if (this.raf) return;
    this.raf = requestAnimationFrame(this.frame);
  }

  private stopAnimation(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.spinVelocity = 0;
    this.tween = undefined;
    this.zoomTween = undefined;
  }

  private readonly frame = (now: number): void => {
    this.raf = 0;
    const dt = Math.min(64, Math.max(1, now - this.lastFrame));
    this.lastFrame = now;

    if (this.zoomTween) {
      const z = this.zoomTween;
      const t = Math.min(1, (now - z.start) / z.duration);
      const scale = z.from * (z.to / z.from) ** easeInOutCubic(t);
      this.setView(
        {
          ...this.view,
          scale,
          tx: z.about.x - z.world.x * scale,
          ty: z.about.y - z.world.y * scale,
        },
        t >= 1,
      );
      if (t >= 1) {
        this.zoomTween = undefined;
        return;
      }
      this.startFrames();
      return;
    }

    if (this.tween) {
      const t = Math.min(1, (now - this.tween.start) / this.tween.duration);
      const rotation = this.tween.from + (this.tween.to - this.tween.from) * easeInOutCubic(t);
      this.setView({ ...this.view, rotation: normalizeAngle(rotation) }, t >= 1);
      if (t >= 1) {
        this.tween = undefined;
        return;
      }
      this.startFrames();
      return;
    }

    if (Math.abs(this.spinVelocity) >= MIN_SPIN_SPEED) {
      this.setView(
        { ...this.view, rotation: normalizeAngle(this.view.rotation + this.spinVelocity * dt) },
        false,
      );
      this.spinVelocity *= FRICTION_PER_FRAME ** (dt / 16.67);
      if (Math.abs(this.spinVelocity) >= MIN_SPIN_SPEED) {
        this.startFrames();
        return;
      }
      this.spinVelocity = 0;
      this.snapIfClose();
      if (!this.tween) {
        this.opts.onView(this.view, true);
        this.settle();
      }
      return;
    }
  };
}

/**
 * Where a ring mark's anchor is in the world: its date angle on the radius it
 * was drawn at, translated by the centre of the ring it belongs to. A mark on
 * the root ring gives back exactly what it always did.
 */
function markWorld(entry: SceneEntryOf<'ringMark'>, scene: Scene): Point | undefined {
  const { angle } = entry.ctx;
  if (typeof angle !== 'number') return undefined;
  const radius = entry.ctx.radius ?? RING_RADIUS;
  const local = polarToXY(angle, radius);
  const ring = entry.ringId ? scene.ringTransform(entry.ringId) : undefined;
  if (!ring || (ring.cx === 0 && ring.cy === 0)) return local;
  return { x: local.x + ring.cx, y: local.y + ring.cy };
}

/**
 * The circle a fragment stands for on the ring, if it stands for one: a band
 * is its circle's, and a ring line is its circle's unless it is the root's.
 */
export function circleOf(entry: SceneEntry): string | null {
  if (entry.kind !== 'band' && entry.kind !== 'ring') return null;
  const ringId = entry.ringId ?? ROOT_RING_ID;
  return ringId === ROOT_RING_ID ? null : ringId;
}

/** Keys pressed while typing belong to the field, whatever the ring would make of them. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLSelectElement) return true;
  return target instanceof HTMLElement && target.isContentEditable;
}

/** Angular width of a set of angles: the circle less its largest empty gap. */
export function angularSpan(angles: number[]): number {
  if (angles.length < 2) return 0;
  const sorted = angles.map(normalizeAngle).sort((a, b) => a - b);
  let largest = sorted[0]! + Math.PI * 2 - sorted[sorted.length - 1]!;
  for (let i = 1; i < sorted.length; i++) largest = Math.max(largest, sorted[i]! - sorted[i - 1]!);
  return Math.PI * 2 - largest;
}

/** World radius the camera should cover at "fit". Exported for tests/tools. */
export const FIT_RADIUS = RING_RADIUS * FIT_HEADROOM;
